/**
 * screens/ObReasonsScreen.tsx — Outbound Reasons (Setup tier, Softknack-only).
 * Reason registry table (code, class [read-only], priority, ceiling, expiry,
 * carve-out, active) + Add-reason modal. Wired to /v1/outbound/reasons.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, ActivityIndicator } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useConsole } from '../context/voiceConsoleContext';
import { Card, Chip, Button, ScreenBody, Modal } from '../components/voiceUi';
import { listOutboundReasons, createOutboundReason, updateOutboundReason } from '../services/voice/voiceService';
import { showSuccess } from '../utils/successHandler';
import { handleApiError } from '../utils/errorHandler';

export default function ObReasonsScreen() {
  const { theme } = useTheme(); const c = theme.colors;
  const { platformRole, orgId } = useConsole();
  const isSoftknack = platformRole === 'platform_admin';

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ reason_code: '', regulatory_class: 'transactional', priority: '1', max_attempts_ceiling: '3', default_expiry_minutes: '1440', description: '', ignores_frequency_cap: false });

  const load = useCallback(async () => {
    setLoading(true);
    // Setup console shows ALL reasons (incl. inactive) so nothing "disappears"
    // when toggled off — and so an inactive reason can be re-activated.
    const { data } = await listOutboundReasons(true);
    setRows(data);
    setLoading(false);
  }, []);
  useEffect(() => { void load(); }, [load, orgId]);

  const toggleActive = async (code: string, isActive: boolean) => {
    try { await updateOutboundReason(code, { is_active: !isActive }); showSuccess(null, 'Reason updated'); void load(); }
    catch (e) { handleApiError(e, 'Action failed'); }
  };

  const create = async () => {
    setBusy(true);
    try {
      await createOutboundReason({
        reason_code: form.reason_code, regulatory_class: form.regulatory_class,
        priority: Number(form.priority), max_attempts_ceiling: Number(form.max_attempts_ceiling),
        default_expiry_minutes: Number(form.default_expiry_minutes), description: form.description,
        ignores_frequency_cap: form.ignores_frequency_cap,
      });
      showSuccess(null, 'Reason added');
      setOpen(false); setForm({ reason_code: '', regulatory_class: 'transactional', priority: '1', max_attempts_ceiling: '3', default_expiry_minutes: '1440', description: '', ignores_frequency_cap: false });
      void load();
    } catch (e) { handleApiError(e, 'Action failed'); } finally { setBusy(false); }
  };

  return (
    <ScreenBody maxWidth={1080}>
      {/* Setup-tier banner (Softknack-only; regulatory class is immutable) */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12, borderWidth: 1, borderColor: c.ink, borderRadius: 10, backgroundColor: c.paper2, marginBottom: 20 }}>
        <Chip kind="solid">Setup tier</Chip>
        <Text style={{ flex: 1, fontSize: 12.5, color: c.ink2 }}>
          Softknack-only registry. Operators can never reclassify <Text style={{ fontWeight: '700' }}>regulatory class</Text> — it decides consent handling downstream.
        </Text>
        {isSoftknack && <Button kind="primary" label="+ Add reason" onPress={() => setOpen(true)} />}
      </View>
      {loading ? <ActivityIndicator color={c.ink3} /> : (
        <Card>
          {rows.map((r, i) => (
            <View key={r.reasonCode} style={{ flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 10, borderTopWidth: i === 0 ? 0 : 1, borderTopColor: c.line, opacity: r.isActive ? 1 : 0.5 }}>
              <Text style={{ width: 180, color: c.ink, fontFamily: theme.font.mono, fontSize: 13 }} numberOfLines={1}>{r.reasonCode}</Text>
              <Chip kind={r.regulatoryClass === 'promotional' ? 'mono' : 'outline'}>{r.regulatoryClass}</Chip>
              <Text style={{ width: 60, color: c.ink3, fontSize: 12 }}>p{r.priority}</Text>
              <Text style={{ width: 70, color: c.ink3, fontSize: 12 }}>≤{r.maxAttemptsCeiling}</Text>
              <Text style={{ width: 90, color: c.ink3, fontSize: 12 }} numberOfLines={1}>{r.defaultExpiryMinutes}m TTL</Text>
              {/* Only shown when the reason bypasses the daily cap — otherwise no clutter. */}
              <View style={{ flex: 1 }}>{r.ignoresFrequencyCap ? <Chip kind="mono">ignores cap</Chip> : null}</View>
              {/* Status badge (read-only) + an explicit action button — so the
                  current state is never confused with the toggle action. */}
              <Chip kind={r.isActive ? 'solid' : 'default'}>{r.isActive ? 'Active' : 'Inactive'}</Chip>
              {isSoftknack && (
                <Button kind="default" label={r.isActive ? 'Deactivate' : 'Activate'} onPress={() => toggleActive(r.reasonCode, r.isActive)} />
              )}
            </View>
          ))}
        </Card>
      )}

      <Modal open={open} onClose={() => setOpen(false)} width={460}>
        <Text style={{ fontSize: 16, fontWeight: '700', color: c.ink, marginBottom: 12 }}>Add reason</Text>
        <F label="Code (lowercase / underscore)"><I value={form.reason_code} onChange={(v) => setForm({ ...form, reason_code: v })} mono /></F>
        <Text style={{ fontSize: 12, color: c.ink2, marginTop: 10, marginBottom: 4 }}>Class</Text>
        <View style={{ flexDirection: 'row', gap: 6 }}>
          {['transactional', 'promotional'].map((v) => {
            const { Pressable } = require('react-native');
            return <Pressable key={v} onPress={() => setForm({ ...form, regulatory_class: v })}><Chip kind={form.regulatory_class === v ? 'solid' : 'default'}>{v}</Chip></Pressable>;
          })}
        </View>
        <F label="Priority"><I value={form.priority} onChange={(v) => setForm({ ...form, priority: v })} numeric /></F>
        <F label="Attempts ceiling"><I value={form.max_attempts_ceiling} onChange={(v) => setForm({ ...form, max_attempts_ceiling: v })} numeric /></F>
        <F label="Expiry TTL (minutes)"><I value={form.default_expiry_minutes} onChange={(v) => setForm({ ...form, default_expiry_minutes: v })} numeric /></F>
        <F label="Description"><I value={form.description} onChange={(v) => setForm({ ...form, description: v })} /></F>
        {/* Cap carve-out. Plain-language so any operator understands the effect:
            ON = this reason can call a customer even after the org's daily call
            cap is hit (for urgent, transactional reasons like a freed-slot offer). */}
        <Text style={{ fontSize: 12, color: c.ink2, marginTop: 12, marginBottom: 4 }}>Ignore daily call cap</Text>
        <View style={{ flexDirection: 'row', gap: 6, alignItems: 'center' }}>
          {(() => { const { Pressable } = require('react-native'); return [false, true].map((v) => (
            <Pressable key={String(v)} onPress={() => setForm({ ...form, ignores_frequency_cap: v })}>
              <Chip kind={form.ignores_frequency_cap === v ? 'solid' : 'default'}>{v ? 'Yes — can exceed cap' : 'No — respects cap'}</Chip>
            </Pressable>
          )); })()}
        </View>
        <Text style={{ fontSize: 11, color: c.ink3, marginTop: 4 }}>
          Leave “No” unless this reason is urgent and time-critical. “Yes” lets it call even after the org’s daily limit is reached.
        </Text>
        <View style={{ marginTop: 14 }}><Button kind="primary" label={busy ? 'Adding…' : 'Add'} onPress={create} disabled={!form.reason_code || busy} /></View>
      </Modal>
    </ScreenBody>
  );
}

function F({ label, children }: { label: string; children: React.ReactNode }) {
  const { theme } = useTheme(); const c = theme.colors;
  return <View style={{ marginTop: 8 }}><Text style={{ fontSize: 12, color: c.ink2, marginBottom: 4 }}>{label}</Text>{children}</View>;
}
function I({ value, onChange, numeric, mono }: { value: string; onChange: (v: string) => void; numeric?: boolean; mono?: boolean }) {
  const { theme } = useTheme(); const c = theme.colors; const { TextInput } = require('react-native');
  return <TextInput value={value} onChangeText={onChange} keyboardType={numeric ? 'numeric' : 'default'} placeholderTextColor={c.ink4}
    style={{ height: 36, paddingHorizontal: 12, borderWidth: 1, borderColor: c.lineStrong, borderRadius: 7, color: c.ink, backgroundColor: c.card, fontFamily: mono ? theme.font.mono : theme.font.sans, fontSize: 13 }} />;
}
